// The widget shelf down the right of a pipeline: the board's widgets, each the same tile as on a canvas (its
// menu, settings, data and Ask your agent), as wide as the shelf, its height dragged at its foot, in the
// order they were added; Add a widget at its foot, and beside it the button that tucks the shelf away to a slim edge
// (Round 7; with no widgets there is no shelf). Widgets added anywhere on this board come here (place).

import { LayoutGrid } from "lucide-react";
import { BUTTON_H } from "../../../shared/catalog";
import type { Board, WidgetItem } from "../../../shared/types";
import { boardItems, set, useStore } from "../../state/store";
import { useShallow } from "zustand/react/shallow";
import { cx } from "../../lib/util";
import { WidgetTile } from "../../widgets/WidgetTile";
import { openContextMenu } from "../../chrome/ContextMenu";
import { MAX_H, MIN_H } from "./place";
import { ShelfHide, useHeightGrip } from "../shelf";

const byPlace = (a: WidgetItem, b: WidgetItem) => a.y - b.y || a.x - b.x || a.createdAt - b.createdAt;

/** The pipeline's widgets, in the shelf's order. */
export function useShelfItems(boardId: string): WidgetItem[] {
  return [...useStore(useShallow((s) => boardItems(s, boardId).filter((i): i is WidgetItem => i.kind === "widget")))].sort(byPlace);
}

export function Shelf({ board }: { board: Board }) {
  const items = useShelfItems(board.id);
  const gallery = useStore((s) => s.panel === "gallery");
  return (
    <aside className="pp-shelf" aria-label="Widgets">
      <div className="pp-shelf-list">
        {items.map((w) => (
          <ShelfTile key={w.id} item={w} />
        ))}
        <div className="shelf-foot">
          <button className={cx("pp-shelf-add", gallery && "is-on")} onClick={() => set((s) => ({ panel: s.panel === "gallery" ? null : "gallery", panelArg: null }))}>
            <LayoutGrid size={14} /> Add a widget
          </button>
          <ShelfHide board={board} />
        </div>
      </div>
    </aside>
  );
}

function ShelfTile({ item }: { item: WidgetItem }) {
  const { drag, grip } = useHeightGrip(item, MIN_H, MAX_H);
  // a Button is one line tall, flush in the column, with no height to drag (Round 7)
  const sized = item.type !== "action";
  const height = sized ? Math.max(MIN_H, Math.min(MAX_H, drag ?? item.h)) : BUTTON_H;
  return (
    <div className={cx("pp-shelf-item", drag !== null && "is-sizing")}>
      <div
        className="pp-shelf-tile"
        style={{ height }}
        data-item-id={item.id}
        onContextMenu={(e) => {
          // a right click opens the widget's own menu, as on a canvas
          if ((e.target as HTMLElement).closest("input, textarea, [contenteditable]")) return;
          e.preventDefault();
          set({ selection: [item.id] });
          openContextMenu({ x: e.clientX, y: e.clientY, kind: "item", ids: [item.id], at: null });
        }}
      >
        <WidgetTile item={item} readOnly={false} />
      </div>
      {sized && grip("pp-shelf-size", height)}
    </div>
  );
}
